import {
  type ComputedRef,
  computed,
  onBeforeUnmount,
  onMounted,
  watch,
} from 'vue';
import { store } from '@/app/store';
import { attractLoop } from '@/services/attract/attract-loop';
import {
  type AttractAssignment,
  msUntilNextUtcHour,
  parseAttractExperiment,
  preloadVideosFor,
  resolveAttractAssignment,
} from '@/services/attract/experiment';
import { captureKioskEvent } from '@/services/telemetry/kiosk-analytics';
import { sentryService } from '@/services/telemetry/sentry';
import type { AttractExperimentVariant } from '@/shared/types/api';

const ATTRACT_RECHECK_MAX_MS = 60_000;
const HOUR_BOUNDARY_SLACK_MS = 500;

type AttractEvaluationReason = 'config' | 'mount' | 'timer';

interface UseAttractLoopOptions {
  isBusy: () => boolean;
}

interface UseAttractLoopResult {
  assignment: ComputedRef<AttractAssignment | null>;
  preloadVideos: ComputedRef<AttractExperimentVariant[]>;
}

function warn(message: string, error: unknown): void {
  // biome-ignore lint/suspicious/noConsole: the attract loop must fall back silently for visitors.
  console.warn(message, error instanceof Error ? error.message : String(error));
}

export function useAttractLoop({
  isBusy,
}: UseAttractLoopOptions): UseAttractLoopResult {
  const rawExperiment = computed(() => store.kiosk?.attract_experiment ?? null);
  const experiment = computed(() =>
    parseAttractExperiment(rawExperiment.value)
  );
  const unit = computed(() => store.kiosk?.slug ?? null);
  const assignment = computed(() => attractLoop.current.value);
  const preloadVideos = computed(() =>
    preloadVideosFor(experiment.value, assignment.value)
  );

  let timer: ReturnType<typeof setTimeout> | null = null;
  let isUnmounted = false;

  function reportIgnoredConfig(): void {
    if (rawExperiment.value === null || experiment.value !== null) {
      return;
    }
    try {
      sentryService.addBreadcrumb(
        'attract',
        'Ignoring malformed attract experiment config',
        { kiosk_slug: unit.value },
        'warning'
      );
    } catch (error) {
      warn('[Attract] Breadcrumb failed:', error);
    }
  }

  function reportSwitch(
    previous: AttractAssignment,
    next: AttractAssignment | null,
    reason: AttractEvaluationReason
  ): void {
    try {
      sentryService.addBreadcrumb('attract', 'Attract loop switched', {
        from_variant: previous.variant,
        to_variant: next?.variant ?? null,
        slot: next?.slot ?? null,
        reason,
      });
    } catch (error) {
      warn('[Attract] Breadcrumb failed:', error);
    }
    captureKioskEvent('attract_variant_switched', {
      from_experiment_key: previous.experimentKey,
      from_variant: previous.variant,
      from_slot: previous.slot,
      reason,
    });
  }

  function evaluate(reason: AttractEvaluationReason): void {
    let next: AttractAssignment | null = null;
    try {
      next = resolveAttractAssignment(experiment.value, unit.value, new Date());
    } catch (error) {
      warn('[Attract] Failed to resolve the attract loop:', error);
    }
    const previous = attractLoop.apply(next);
    if (
      previous &&
      (previous.experimentKey !== next?.experimentKey ||
        previous.variant !== next.variant)
    ) {
      reportSwitch(previous, next, reason);
    }
  }

  function scheduleNextCheck(): void {
    const delay = Math.min(
      msUntilNextUtcHour(new Date()) + HOUR_BOUNDARY_SLACK_MS,
      ATTRACT_RECHECK_MAX_MS
    );
    timer = setTimeout(() => {
      timer = null;
      if (isUnmounted) {
        return;
      }
      if (!isBusy()) {
        evaluate('timer');
      }
      scheduleNextCheck();
    }, delay);
  }

  evaluate('mount');
  reportIgnoredConfig();

  watch([experiment, unit], () => {
    if (isUnmounted || isBusy()) {
      return;
    }
    reportIgnoredConfig();
    evaluate('config');
  });

  onMounted(() => {
    if (!isUnmounted) {
      scheduleNextCheck();
    }
  });

  onBeforeUnmount(() => {
    isUnmounted = true;
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  });

  return { assignment, preloadVideos };
}
